import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/shared/helpers/cn';
import styles from './Tabs.module.scss';

export interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: readonly TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  /** Rotulo do conjunto de abas, para leitores de tela. */
  label: string;
  /**
   * Conteudo na mesma linha das abas, antes delas.
   *
   * Para o controle que governa o que as abas mostram — a navegacao de dia da
   * agenda, por exemplo — sem gastar uma faixa propria da tela com ele.
   */
  leading?: ReactNode;
  /**
   * Conteudo acima das abas, encostado a direita.
   *
   * Para os controles que pertencem ao conteudo das abas — a busca e as
   * configuracoes da agenda, por exemplo — sem gastar outra faixa da tela.
   */
  toolbar?: ReactNode;
  /**
   * Onde a `toolbar` entra: acima das abas ou abaixo delas.
   *
   * `below` para o controle que age sobre o **conteudo** da aba escolhida — a
   * busca da listagem, por exemplo: ali ele fica entre a aba e o que ela
   * mostra, na ordem em que se le a tela.
   */
  toolbarPlacement?: 'above' | 'below';
  /**
   * Conteudo na mesma linha das abas, encostado a direita.
   *
   * Para o controle que age sobre o conteudo de qualquer aba — a ordenacao de
   * uma listagem, por exemplo — sem gastar uma faixa propria da tela e sem
   * disputar a esquerda com as abas.
   */
  trailing?: ReactNode;
  /**
   * `linha` sublinha a aba ativa sobre uma linha de base; `botoes` desenha cada
   * aba como um botao e preenche o ativo, sem linha nenhuma.
   */
  variant?: 'linha' | 'botoes';
  /** Mostra o `label` como rotulo visivel acima das abas. */
  showLabel?: boolean;
  className?: string;
}

/**
 * Abas com navegacao por teclado.
 *
 * Segue o padrao ARIA: setas movem entre abas, `Home`/`End` vao aos extremos, e
 * so a aba ativa participa da ordem de tabulacao (`tabindex` roving). Sem isso,
 * um conjunto de abas obriga o usuario de teclado a passar por todas para chegar
 * ao conteudo.
 */
export function Tabs({
  items,
  activeId,
  onChange,
  label,
  leading,
  toolbar,
  toolbarPlacement = 'above',
  trailing,
  variant = 'linha',
  showLabel,
  className,
}: TabsProps) {
  const baseId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  const enabled = items.filter((item) => !item.disabled);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const currentIndex = enabled.findIndex((item) => item.id === activeId);
    if (currentIndex === -1) return;

    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabled.length;
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + enabled.length) % enabled.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = enabled.length - 1;

    if (nextIndex === null) return;
    event.preventDefault();

    const next = enabled[nextIndex];
    if (!next) return;
    onChange(next.id);
    listRef.current?.querySelector<HTMLElement>(`#${CSS.escape(`${baseId}-tab-${next.id}`)}`)?.focus();
  };

  const active = items.find((item) => item.id === activeId);

  const grupoAbas = (
    /* Rótulo colado na lista que ele nomeia, não na barra inteira. */
    <div className={styles.grupoAbas}>
      {showLabel ? (
        <span id={`${baseId}-label`} className={styles.rotulo}>
          {label}
        </span>
      ) : null}

      <div
        ref={listRef}
        role="tablist"
        aria-label={showLabel ? undefined : label}
        aria-labelledby={showLabel ? `${baseId}-label` : undefined}
        className={cn(styles.list, variant === 'botoes' && styles.listaBotoes)}
        onKeyDown={handleKeyDown}
      >
        {items.map((item) => {
          const selected = item.id === activeId;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`${baseId}-tab-${item.id}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              disabled={item.disabled}
              className={cn(
                styles.tab,
                variant === 'botoes' && styles.tabBotao,
                selected && (variant === 'botoes' ? styles.botaoAtivo : styles.selected),
              )}
              onClick={() => onChange(item.id)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={cn(styles.root, className)}>
      <div className={cn(styles.barra, variant === 'botoes' && styles.barraPeloTopo)}>
        {leading ? <div className={styles.leading}>{leading}</div> : null}

        <div className={cn(styles.grupoLista, variant === 'botoes' && styles.grupoSolto)}>
          {toolbar && toolbarPlacement === 'above' ? <div className={styles.toolbar}>{toolbar}</div> : null}

          {/*
            Só quem passa `trailing` ganha a linha extra: sem ela, a árvore
            continua a mesma das telas que já usam as abas.
          */}
          {trailing ? (
            <div className={styles.linhaDeAbas}>
              {grupoAbas}
              <div className={styles.trailing}>{trailing}</div>
            </div>
          ) : (
            grupoAbas
          )}

          {toolbar && toolbarPlacement === 'below' ? <div className={styles.toolbar}>{toolbar}</div> : null}
        </div>
      </div>

      {active ? (
        <div
          role="tabpanel"
          id={`${baseId}-panel-${active.id}`}
          aria-labelledby={`${baseId}-tab-${active.id}`}
          tabIndex={0}
          className={styles.panel}
        >
          {active.content}
        </div>
      ) : null}
    </div>
  );
}
